import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { appendAuthToken } from "../utils/auth";
import VoucherSimpleManager from "./VoucherSimpleManager";

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const emptyExpenseRow = () => ({
  expenseType: "",
  amount: "",
  description: ""
});

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2
  });
};

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB");
};

const getExpenseMode = (record = {}) =>
  record.expenseMode === "complaint" || record.entryType === "complaint" ? "complaint" : "general";

const getEntryLabel = (record = {}) =>
  getExpenseMode(record) === "complaint" ? "Complaint Expense" : "General Expense";

const getPersonKey = (person = {}) => person.engineerId || person.email || person.name || person._id || "";

const normalizeLookup = (value = "") => String(value || "").trim().toLowerCase();

const matchesLookup = (record = {}, fields = [], lookups = []) => {
  const normalizedLookups = lookups.map(normalizeLookup).filter(Boolean);
  if (normalizedLookups.length === 0) return false;

  return fields.some((field) => normalizedLookups.includes(normalizeLookup(record[field])));
};

const personMatchesSelection = (person = {}, selection = "") =>
  matchesLookup(
    {
      _id: person._id,
      engineerId: person.engineerId,
      email: person.email,
      name: person.name
    },
    ["_id", "engineerId", "email", "name"],
    [selection]
  );

const isAccountsOwned = (record = {}, accountLookups = []) =>
  record.ownerRole === "accounts" &&
  matchesLookup(record, ["accountId", "accountName"], accountLookups);

const hasEngineerReference = (record = {}) =>
  Boolean(normalizeLookup(record.engineerId) || normalizeLookup(record.engineerName));

const isEngineerOwned = (record = {}, engineerLookups = []) =>
  (record.ownerRole === "engineer" || !record.ownerRole) &&
  matchesLookup(record, ["engineerId", "engineerName"], engineerLookups);

const buildLedgerRows = (expenses = [], allowances = []) => {
  const expenseRows = expenses.map((item) => ({
    ...item,
    kind: "expense",
    date: item.voucherDate,
    complaintGeneral: getEntryLabel(item),
    badgeTone: getExpenseMode(item) === "complaint" ? "sky" : "emerald",
    refText: [item.complaintRefId, item.company || item.location || item.accountName || item.engineerName].filter(Boolean).join(" / ") || "General expense",
    details: [{
      title: item.expenseType || "Expense",
      meta: item.description || "",
      amount: Number(item.amount || 0)
    }],
    expenseAmount: Number(item.amount || 0),
    allowanceAmount: 0,
    amountEffect: -Number(item.amount || 0)
  }));

  const allowanceRows = allowances.map((item) => ({
    ...item,
    kind: "allowance",
    date: item.allowanceDate,
    complaintGeneral: getEntryLabel(item),
    badgeTone: item.entryType === "complaint" ? "sky" : "emerald",
    refText: [item.complaintRefId, item.company || item.location || item.accountName || item.engineerName].filter(Boolean).join(" / ") || "Allowance",
    details: [{
      title: `Allowance from ${(item.givenByRole || "Allowance").toUpperCase()}`,
      meta: [item.givenByName, item.notes].filter(Boolean).join(" - ") || "Allowance received",
      amount: Number(item.amount || 0)
    }],
    expenseAmount: 0,
    allowanceAmount: Number(item.amount || 0),
    amountEffect: Number(item.amount || 0)
  }));

  let runningBalance = 0;
  return [...expenseRows, ...allowanceRows]
    .sort((first, second) => {
      const firstDate = new Date(first.date || first.createdAt || 0).getTime();
      const secondDate = new Date(second.date || second.createdAt || 0).getTime();
      if (firstDate !== secondDate) return firstDate - secondDate;
      return new Date(first.createdAt || 0).getTime() - new Date(second.createdAt || 0).getTime();
    })
    .map((row) => {
      runningBalance += Number(row.amountEffect || 0);
      return {
        ...row,
        computedBalance: runningBalance
      };
    })
    .reverse();
};

const filterLedgerRows = (rows, filters) => {
  const query = filters.search.trim().toLowerCase();

  return rows.filter((row) => {
    const dateValue = String(row.date || "").slice(0, 10);
    if (filters.fromDate && dateValue < filters.fromDate) return false;
    if (filters.toDate && dateValue > filters.toDate) return false;
    if (filters.kind !== "all" && row.kind !== filters.kind) return false;
    if (!query) return true;

    return [
      formatDisplayDate(row.date),
      row.kind,
      row.complaintGeneral,
      row.refText,
      row.details?.map((item) => `${item.title} ${item.meta} ${item.amount}`).join(" "),
      row.expenseAmount,
      row.allowanceAmount,
      row.computedBalance,
      row.accountName,
      row.engineerName
    ].join(" ").toLowerCase().includes(query);
  });
};

function LegacyAccountsVouchers() {
  const role = localStorage.getItem("role") || "accounts";
  const isAdmin = role === "admin";
  const isAccounts = role === "accounts";
  const currentAccount = useMemo(() => ({
    _id: localStorage.getItem("accountsEmail") || localStorage.getItem("accountsName") || "",
    name: localStorage.getItem("accountsName") || "Accounts",
    email: localStorage.getItem("accountsEmail") || ""
  }), []);

  const [accountsUsers, setAccountsUsers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(getPersonKey(currentAccount));
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [accountVouchers, setAccountVouchers] = useState([]);
  const [accountAllowances, setAccountAllowances] = useState([]);
  const [engineerVouchers, setEngineerVouchers] = useState([]);
  const [engineerAllowances, setEngineerAllowances] = useState([]);
  const [expenseDate, setExpenseDate] = useState(todayInputValue());
  const [allowanceDate, setAllowanceDate] = useState(todayInputValue());
  const [engineerAllowanceDate, setEngineerAllowanceDate] = useState(todayInputValue());
  const [expenseRows, setExpenseRows] = useState([emptyExpenseRow()]);
  const [accountAllowanceAmount, setAccountAllowanceAmount] = useState("");
  const [engineerAllowanceAmount, setEngineerAllowanceAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [engineerNotes, setEngineerNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingExpense, setSavingExpense] = useState(false);
  const [savingAllowance, setSavingAllowance] = useState(false);
  const [savingEngineerAllowance, setSavingEngineerAllowance] = useState(false);
  const [showAllowanceForm, setShowAllowanceForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [allowanceTarget, setAllowanceTarget] = useState(isAdmin ? "accounts" : "engineer");
  const [showAccountsTable, setShowAccountsTable] = useState(true);
  const [showEngineerTable, setShowEngineerTable] = useState(false);
  const [accountFilters, setAccountFilters] = useState({
    fromDate: "",
    toDate: "",
    search: "",
    kind: "all"
  });
  const [engineerFilters, setEngineerFilters] = useState({
    fromDate: "",
    toDate: "",
    search: "",
    kind: "all"
  });

  const selectedAccountDetails = useMemo(() => {
    if (isAccounts) return currentAccount;
    return accountsUsers.find((account) => personMatchesSelection(account, selectedAccount)) || {};
  }, [accountsUsers, currentAccount, isAccounts, selectedAccount]);

  const selectedEngineerDetails = useMemo(
    () => engineers.find((engineer) => personMatchesSelection(engineer, selectedEngineer)) || {},
    [engineers, selectedEngineer]
  );

  const accountLookup = isAccounts ? getPersonKey(currentAccount) : selectedAccount;
  const accountName = selectedAccountDetails.name || selectedAccountDetails.email || accountLookup || "";
  const engineerLookup = selectedEngineer;
  const engineerName = selectedEngineerDetails.name || selectedEngineerDetails.email || engineerLookup || "";
  const accountLookups = useMemo(
    () => [accountLookup, selectedAccountDetails._id, selectedAccountDetails.email, selectedAccountDetails.name],
    [accountLookup, selectedAccountDetails._id, selectedAccountDetails.email, selectedAccountDetails.name]
  );
  const engineerLookups = useMemo(
    () => [engineerLookup, selectedEngineerDetails._id, selectedEngineerDetails.engineerId, selectedEngineerDetails.email, selectedEngineerDetails.name],
    [engineerLookup, selectedEngineerDetails._id, selectedEngineerDetails.engineerId, selectedEngineerDetails.email, selectedEngineerDetails.name]
  );

  const loadPeople = useCallback(async () => {
    const requests = [axios.get(API_BASE_URL + "/api/engineers")];
    if (isAdmin) requests.push(axios.get(API_BASE_URL + "/api/accounts"));

    const results = await Promise.allSettled(requests);
    const engineerRows = results[0].status === "fulfilled" && Array.isArray(results[0].value.data)
      ? results[0].value.data
      : [];
    setEngineers(engineerRows);

    if (isAdmin) {
      const accountRows = results[1]?.status === "fulfilled" && Array.isArray(results[1].value.data)
        ? results[1].value.data
        : [];
      setAccountsUsers(accountRows);
      if (!selectedAccount && accountRows.length > 0) {
        setSelectedAccount(getPersonKey(accountRows[0]));
      }
    }
  }, [isAdmin, selectedAccount]);

  const loadVoucherData = useCallback(async () => {
    try {
      setLoading(true);
      const requests = [];

      if (accountLookup) {
        requests.push(
          axios.get(API_BASE_URL + "/api/vouchers", { params: { ownerRole: "accounts" } }),
          axios.get(API_BASE_URL + "/api/vouchers/allowances", { params: { ownerRole: "accounts" } })
        );
      }

      if (engineerLookup) {
        requests.push(
          axios.get(API_BASE_URL + "/api/vouchers", { params: { ownerRole: "engineer" } }),
          axios.get(API_BASE_URL + "/api/vouchers/allowances", { params: { ownerRole: "engineer" } })
        );
      }

      const results = await Promise.allSettled(requests);
      let index = 0;

      if (accountLookup) {
        const vouchers = results[index]?.status === "fulfilled" && Array.isArray(results[index].value.data) ? results[index].value.data : [];
        setAccountVouchers(vouchers.filter((item) => isAccountsOwned(item, accountLookups) && !hasEngineerReference(item)));
        index += 1;
        const allowances = results[index]?.status === "fulfilled" && Array.isArray(results[index].value.data) ? results[index].value.data : [];
        setAccountAllowances(allowances.filter((item) => isAccountsOwned(item, accountLookups) && !hasEngineerReference(item) && (normalizeLookup(item.givenByRole) === "admin" || normalizeLookup(item.givenByRole) === "accounts")));
        index += 1;
      } else {
        setAccountVouchers([]);
        setAccountAllowances([]);
      }

      if (engineerLookup) {
        const vouchers = results[index]?.status === "fulfilled" && Array.isArray(results[index].value.data) ? results[index].value.data : [];
        setEngineerVouchers(vouchers.filter((item) => isEngineerOwned(item, engineerLookups)));
        index += 1;
        const allowances = results[index]?.status === "fulfilled" && Array.isArray(results[index].value.data) ? results[index].value.data : [];
        setEngineerAllowances(allowances.filter((item) => isEngineerOwned(item, engineerLookups)));
      } else {
        setEngineerVouchers([]);
        setEngineerAllowances([]);
      }
    } catch (error) {
      setAccountVouchers([]);
      setAccountAllowances([]);
      setEngineerVouchers([]);
      setEngineerAllowances([]);
    } finally {
      setLoading(false);
    }
  }, [accountLookup, accountLookups, engineerLookup, engineerLookups]);

  useEffect(() => {
    loadPeople();
  }, [loadPeople]);

  useEffect(() => {
    loadVoucherData();
  }, [loadVoucherData]);

  // Accounts and Admin can both target Accounts or Engineer for Credit creation

  const accountTotals = useMemo(() => {
    const allowance = accountAllowances.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const expense = accountVouchers.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    return { allowance, expense, balance: allowance - expense };
  }, [accountAllowances, accountVouchers]);

  const engineerTotals = useMemo(() => {
    const allowance = engineerAllowances.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const expense = engineerVouchers.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    return { allowance, expense, balance: allowance - expense };
  }, [engineerAllowances, engineerVouchers]);

  const visibleAccounts = useMemo(() => accountsUsers.slice(0, 6), [accountsUsers]);

  const accountRows = useMemo(
    () => filterLedgerRows(buildLedgerRows(accountVouchers, accountAllowances), accountFilters),
    [accountAllowances, accountFilters, accountVouchers]
  );

  const engineerRows = useMemo(
    () => filterLedgerRows(buildLedgerRows(engineerVouchers, engineerAllowances), engineerFilters),
    [engineerAllowances, engineerFilters, engineerVouchers]
  );

  const updateExpenseRow = (index, field, value) => {
    setExpenseRows((current) =>
      current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row)
    );
  };

  const addExpenseRow = () => setExpenseRows((current) => [...current, emptyExpenseRow()]);

  const removeExpenseRow = (index) => {
    setExpenseRows((current) => current.length === 1 ? [emptyExpenseRow()] : current.filter((_, rowIndex) => rowIndex !== index));
  };

  const saveAccountExpenses = async () => {
    const rows = expenseRows
      .filter((row) => row.expenseType || row.amount || row.description)
      .map((row, index) => ({
        ...row,
        expenseType: String(row.expenseType || "").trim(),
        amount: Number(row.amount),
        rowNumber: index + 1
      }));

    if (!accountLookup) {
      alert("Select an accounts user first");
      return;
    }

    const invalidRow = rows.find((row) => !row.expenseType || !Number.isFinite(row.amount) || row.amount <= 0);
    if (rows.length === 0 || invalidRow) {
      alert(`Please complete expense type and amount${invalidRow ? ` in row ${invalidRow.rowNumber}` : ""}`);
      return;
    }

    try {
      setSavingExpense(true);
      await axios.post(API_BASE_URL + "/api/vouchers", {
        ownerRole: "accounts",
        accountId: accountLookup,
        accountName,
        createdByUserId: accountLookup,
        voucherDate: expenseDate,
        entryType: "non-complaint",
        expenseMode: "general",
        location: "Expense",
        entries: rows.map((row) => ({
          expenseType: row.expenseType,
          amount: row.amount,
          description: row.description
        }))
      });
      setExpenseRows([emptyExpenseRow()]);
      await loadVoucherData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save accounts expense");
    } finally {
      setSavingExpense(false);
    }
  };

  const saveAccountAllowance = async () => {
    const amount = Number(accountAllowanceAmount);
    if (!accountLookup) {
      alert("Select an accounts user first");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Please enter a valid credit amount");
      return;
    }

    try {
      setSavingAllowance(true);
      await axios.post(API_BASE_URL + "/api/vouchers/allowances", {
        ownerRole: "accounts",
        accountId: accountLookup,
        accountName,
        entryType: "non-complaint",
        location: `${isAdmin ? "Admin" : "Accounts"} credit`,
        allowanceDate,
        amount,
        givenByRole: isAdmin ? "admin" : "accounts",
        givenByName: isAdmin ? (localStorage.getItem("adminName") || "Admin") : accountName,
        notes
      });
      setAccountAllowanceAmount("");
      setNotes("");
      await loadVoucherData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save accounts credit");
    } finally {
      setSavingAllowance(false);
    }
  };

  const saveEngineerAllowance = async () => {
    const amount = Number(engineerAllowanceAmount);
    if (!engineerLookup) {
      alert("Select an engineer first");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Please enter a valid engineer allowance amount");
      return;
    }

    try {
      setSavingEngineerAllowance(true);
      await axios.post(API_BASE_URL + "/api/vouchers/allowances", {
        ownerRole: "engineer",
        engineerId: engineerLookup,
        engineerName,
        entryType: "non-complaint",
        location: `${isAdmin ? "Admin" : "Accounts"} allowance`,
        allowanceDate: engineerAllowanceDate,
        amount,
        givenByRole: isAdmin ? "admin" : "accounts",
        givenByName: isAdmin ? (localStorage.getItem("adminName") || "Admin") : accountName,
        notes: engineerNotes
      });
      setEngineerAllowanceAmount("");
      setEngineerNotes("");
      await loadVoucherData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save engineer allowance");
    } finally {
      setSavingEngineerAllowance(false);
    }
  };

  const saveSelectedAllowance = () => {
    if (allowanceTarget === "accounts") {
      saveAccountAllowance();
      return;
    }

    saveEngineerAllowance();
  };

  const downloadStatementPdf = ({ ownerRole, lookup, label }) => {
    if (!lookup) {
      alert(`Select ${label} first`);
      return;
    }

    const params = new URLSearchParams({
      ownerRole,
      download: "1"
    });

    if (ownerRole === "accounts") {
      params.set("account", lookup);
      params.set("accountId", lookup);
    } else {
      params.set("engineer", lookup);
      params.set("engineerId", lookup);
    }

    window.open(appendAuthToken(API_BASE_URL + `/api/vouchers/statement/pdf?${params.toString()}`), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mobile-dense-page mx-auto max-w-7xl space-y-4 lg:space-y-6">
      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">Vouchers</p>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">{isAdmin ? "Accounts & Engineer Vouchers" : "Accounts Voucher"}</h1>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            {isAccounts && (
              <button
                type="button"
                onClick={() => setShowExpenseForm((current) => !current)}
                className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                {showExpenseForm ? "Hide Debit Form" : "+ Add Debit"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowAllowanceForm((current) => !current)}
              className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              {showAllowanceForm ? "Hide Credit Form" : "+ Add Credit"}
            </button>
            <button
              type="button"
              onClick={() => setShowAccountsTable((current) => !current)}
              className={`rounded-xl px-5 py-3 text-sm font-semibold transition ${showAccountsTable ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            >
              {showAccountsTable ? "Hide Accounts" : "View Accounts"}
            </button>
            <button
              type="button"
              onClick={() => setShowEngineerTable((current) => !current)}
              className={`rounded-xl px-5 py-3 text-sm font-semibold transition ${showEngineerTable ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            >
              {showEngineerTable ? "Hide Engineer" : "View Engineer"}
            </button>
            <div className="min-w-[240px]">
              <SelectField label="Engineer" value={selectedEngineer} onChange={(event) => {
                setSelectedEngineer(event.target.value);
                if (event.target.value) setShowEngineerTable(true);
              }}>
                <option value="">Select engineer</option>
                {engineers.map((engineer) => (
                  <option key={engineer._id} value={getPersonKey(engineer)}>{engineer.name || engineer.email}</option>
                ))}
              </SelectField>
            </div>
          </div>
        </div>
      </section>

      {showAllowanceForm && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Add Credit</h2>
            </div>
            <button
              type="button"
              onClick={() => setShowAllowanceForm(false)}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-5">
            <SelectField
              label="Credit For"
              value={allowanceTarget}
              onChange={(event) => setAllowanceTarget(event.target.value)}
            >
              <option value="accounts">Accounts</option>
              <option value="engineer">Engineer</option>
            </SelectField>

            {allowanceTarget === "accounts" ? (
              isAdmin ? (
                <SelectField label="Accounts User" value={selectedAccount} onChange={(event) => setSelectedAccount(event.target.value)}>
                  <option value="">Select accounts</option>
                  {accountsUsers.map((account) => (
                    <option key={account._id} value={getPersonKey(account)}>{account.name || account.email}</option>
                  ))}
                </SelectField>
              ) : (
                <InputField label="Accounts User" value={currentAccount.name || currentAccount.email} disabled />
              )
            ) : (
              <SelectField label="Engineer" value={selectedEngineer} onChange={(event) => setSelectedEngineer(event.target.value)}>
                <option value="">Select engineer</option>
                {engineers.map((engineer) => (
                  <option key={engineer._id} value={getPersonKey(engineer)}>{engineer.name || engineer.email}</option>
                ))}
              </SelectField>
            )}

            <InputField
              label="Date"
              type="date"
              value={allowanceTarget === "accounts" ? allowanceDate : engineerAllowanceDate}
              onChange={(event) => allowanceTarget === "accounts" ? setAllowanceDate(event.target.value) : setEngineerAllowanceDate(event.target.value)}
            />
            <InputField
              label="Amount"
              type="number"
              min="0"
              step="0.01"
              value={allowanceTarget === "accounts" ? accountAllowanceAmount : engineerAllowanceAmount}
              onChange={(event) => allowanceTarget === "accounts" ? setAccountAllowanceAmount(event.target.value) : setEngineerAllowanceAmount(event.target.value)}
            />
            <InputField
              label="Notes"
              value={allowanceTarget === "accounts" ? notes : engineerNotes}
              onChange={(event) => allowanceTarget === "accounts" ? setNotes(event.target.value) : setEngineerNotes(event.target.value)}
            />
          </div>

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={saveSelectedAllowance}
              disabled={savingAllowance || savingEngineerAllowance}
              className="rounded-xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white disabled:bg-slate-400"
            >
              {savingAllowance || savingEngineerAllowance ? "Saving..." : "Save Credit"}
            </button>
          </div>
        </section>
      )}

      {showAccountsTable && isAdmin && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Accounts Details</h2>
              <p className="mt-1 text-sm text-slate-500">Quick view of accountant users and contact details.</p>
            </div>
            <div className="w-full lg:max-w-md">
              <SelectField label="Accounts User" value={selectedAccount} onChange={(event) => setSelectedAccount(event.target.value)}>
                <option value="">Select accounts</option>
                {accountsUsers.map((account) => (
                  <option key={account._id} value={getPersonKey(account)}>{account.name || account.email}</option>
                ))}
              </SelectField>
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleAccounts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-10 text-center text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                No accounts found yet.
              </div>
            ) : (
              visibleAccounts.map((account) => (
                <article
                  key={account._id}
                  className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(239,246,255,0.7))] p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Account User</p>
                      <h3 className="mt-1 truncate text-lg font-bold text-slate-900">{account.name || "-"}</h3>
                    </div>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {(account.name || "A").charAt(0).toUpperCase()}
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="rounded-xl border border-slate-100 bg-white px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Email</p>
                      <p className="mt-1 break-words text-sm font-medium text-slate-700">{account.email || "-"}</p>
                    </div>
                    <div className="rounded-xl border border-slate-100 bg-white px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Account ID</p>
                      <p className="mt-1 break-words text-sm font-medium text-slate-700">{account._id || "-"}</p>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      )}

      {isAccounts && showExpenseForm && (
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Debit</h2>
            </div>
            <div className="flex items-end gap-3">
              <InputField label="Debit Date" type="date" value={expenseDate} onChange={(event) => setExpenseDate(event.target.value)} />
              <button
                type="button"
                onClick={() => setShowExpenseForm(false)}
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>

          <ExpenseRows rows={expenseRows} updateRow={updateExpenseRow} addRow={addExpenseRow} removeRow={removeExpenseRow} />

          <div className="mt-5 flex justify-end">
            <button type="button" onClick={saveAccountExpenses} disabled={savingExpense} className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:bg-slate-400">
              {savingExpense ? "Saving..." : "Save Debit"}
            </button>
          </div>
        </section>
      )}

      {showAccountsTable && (
        <LedgerTable
          title="Accounts Voucher Table"
          loading={loading}
          rows={accountRows}
          filters={accountFilters}
          setFilters={setAccountFilters}
          totals={accountTotals}
          onDownload={() => downloadStatementPdf({ ownerRole: "accounts", lookup: accountLookup, label: "an accounts user" })}
        />
      )}

      {showEngineerTable && selectedEngineer ? (
        <LedgerTable
          title={`Engineer Voucher Table${engineerName ? ` - ${engineerName}` : ""}`}
          loading={loading}
          rows={engineerRows}
          filters={engineerFilters}
          setFilters={setEngineerFilters}
          totals={engineerTotals}
          onDownload={() => downloadStatementPdf({ ownerRole: "engineer", lookup: engineerLookup, label: "an engineer" })}
        />
      ) : showEngineerTable ? (
        <section className="rounded-[28px] border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
          Select an engineer to view engineer debit and credit transactions.
        </section>
      ) : null}
    </div>
  );
}

function ExpenseRows({ rows, updateRow, addRow, removeRow }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-50 text-left text-slate-700">
            <tr>
              <th className="px-3 py-3">S.No</th>
              <th className="px-3 py-3">Debit Type</th>
              <th className="px-3 py-3">Amount</th>
              <th className="px-3 py-3">Remarks</th>
              <th className="px-3 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map((row, index) => (
              <tr key={index}>
                <td className="px-3 py-3 font-semibold">{index + 1}</td>
                <td className="px-3 py-3"><TableInput value={row.expenseType} onChange={(event) => updateRow(index, "expenseType", event.target.value)} placeholder="Debit type" /></td>
                <td className="px-3 py-3"><TableInput type="number" min="0" step="0.01" value={row.amount} onChange={(event) => updateRow(index, "amount", event.target.value)} placeholder="0.00" /></td>
                <td className="px-3 py-3"><TableInput value={row.description} onChange={(event) => updateRow(index, "description", event.target.value)} placeholder="Remarks" /></td>
                <td className="px-3 py-3">
                  <button type="button" onClick={() => removeRow(index)} className="rounded-xl bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-slate-200 p-3">
        <button type="button" onClick={addRow} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">+ Add Debit Row</button>
      </div>
    </div>
  );
}

function LedgerTable({ title, loading, rows, filters, setFilters, totals, onDownload }) {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">
            Total Credit: {formatCurrency(totals.allowance)} | Total Debit: {formatCurrency(totals.expense)} | Current Balance: {formatCurrency(totals.balance)}
          </p>
        </div>
        <div className="flex flex-col gap-3 lg:items-end">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onDownload}
              className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Download PDF
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-4 lg:w-[760px]">
          <InputField label="From Date" type="date" value={filters.fromDate} onChange={(event) => setFilters((current) => ({ ...current, fromDate: event.target.value }))} />
          <InputField label="To Date" type="date" value={filters.toDate} onChange={(event) => setFilters((current) => ({ ...current, toDate: event.target.value }))} />
          <SelectField label="Type" value={filters.kind} onChange={(event) => setFilters((current) => ({ ...current, kind: event.target.value }))}>
            <option value="all">All</option>
            <option value="expense">Debit (Expense)</option>
            <option value="allowance">Credit (Allowance)</option>
          </SelectField>
          <InputField label="Search" value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Search" />
          </div>
        </div>
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-[13px] text-slate-700">
            <thead className="bg-slate-50 text-left text-slate-700">
              <tr>
                <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 font-bold whitespace-nowrap">Date</th>
                <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 font-bold whitespace-nowrap">Type</th>
                <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 font-bold">Remarks</th>
                <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 text-right font-bold whitespace-nowrap">Debit</th>
                <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 text-right font-bold whitespace-nowrap">Credit</th>
                <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 text-right font-bold whitespace-nowrap">Balance</th>
                <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 text-center font-bold whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No entries found.</td></tr>
              ) : rows.map((row) => (
                <tr key={`${row.kind}-${row._id}`} className="transition hover:bg-slate-50/80">
                  <td className="align-middle px-3 py-3 font-medium text-slate-800 whitespace-nowrap">
                    {formatDisplayDate(row.date)}
                    <div className="mt-1 text-[11px] text-slate-500 whitespace-nowrap">
                      {row.kind === "expense" ? `ID: ${row.expenseGroupId || "-"}` : "Allowance"}
                    </div>
                  </td>
                  <td className="align-middle px-3 py-3">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${row.badgeTone === "sky" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {row.complaintGeneral}
                    </span>
                    <div className="mt-1 text-[11px] text-slate-500 line-clamp-1">{row.refText || "-"}</div>
                  </td>
                  <td className="align-middle px-3 py-3 text-slate-700">
                    <div className="rounded-xl bg-slate-50 px-3 py-2 text-[12px] leading-[1.35] text-slate-600 line-clamp-2">
                      {row.details?.map((item) => [item.title, item.meta].filter(Boolean).join(" - ")).join(" | ") || "-"}
                    </div>
                  </td>
                  <td className="align-middle px-3 py-3 text-right font-semibold text-rose-700 whitespace-nowrap">
                    {row.kind === "expense" ? "- " + formatCurrency(row.expenseAmount) : "-"}
                  </td>
                  <td className="align-middle px-3 py-3 text-right font-semibold text-emerald-700 whitespace-nowrap">
                    {row.kind === "allowance" ? "+ " + formatCurrency(row.allowanceAmount) : "-"}
                  </td>
                  <td className={`align-middle px-3 py-3 text-right font-bold whitespace-nowrap ${row.computedBalance < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {formatCurrency(row.computedBalance)}
                  </td>
                  <td className="align-middle px-3 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        const targetId = row.expenseGroupId || row._id;
                        if (targetId) {
                          window.open(appendAuthToken(API_BASE_URL + `/api/vouchers/${targetId}/pdf?download=1`), "_blank", "noopener,noreferrer");
                        }
                      }}
                      className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                      title="Download Voucher Slip PDF"
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function InputField({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-slate-700">{label}</span>
      <input {...props} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100" />
    </label>
  );
}

function SelectField({ label, children, ...props }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-slate-700">{label}</span>
      <select {...props} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100">
        {children}
      </select>
    </label>
  );
}

function TableInput(props) {
  return (
    <input {...props} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100" />
  );
}

export default function AccountsVouchers() {
  const [useRedesigned, setUseRedesigned] = useState(true);

  if (useRedesigned) {
    return (
      <div className="space-y-4">
        <VoucherSimpleManager />
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto p-4 mb-2 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between">
        <span className="text-xs font-bold text-indigo-800">You are currently viewing the legacy voucher view.</span>
        <button
          onClick={() => setUseRedesigned(true)}
          className="px-3 py-1 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-xs"
        >
          Open Redesigned Financial System
        </button>
      </div>
      <LegacyAccountsVouchers />
    </div>
  );
}

